import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Copy, Download, Search, Filter, CheckCircle,
  MessageSquare, Phone, Mail, ClipboardList, ArrowLeft,
  Globe, ChevronDown, ChevronUp, Tag, Layers,
  Zap, Shield, Users, BookOpen, BarChart2, AlertCircle,
  Clock, TrendingUp, Package
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Template {
  id: string;
  title: string;
  titleAr: string;
  category: string;
  categoryAr: string;
  type: 'whatsapp' | 'sms' | 'email' | 'script' | 'checklist' | 'form' | 'report';
  stage: string;
  stageAr: string;
  stageOrder: number;
  language: 'en' | 'ar' | 'both';
  tags: string[];
  contentEn: string;
  contentAr?: string;
  variables: string[];
  notes?: string;
  notesAr?: string;
}

interface ToolCard {
  id: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  category: string;
  categoryAr: string;
  icon: React.ReactNode;
  link?: string;
  items: string[];
  itemsAr: string[];
  color: string;
}

// ─── Template Data ─────────────────────────────────────────────────────────────
const TEMPLATES: Template[] = [
  // ── WhatsApp Templates ──
  {
    id: 'wa-lead-ack-ar',
    title: 'Lead Acknowledgment (Arabic)',
    titleAr: 'تأكيد استلام طلب العميل (عربي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Pre-Stage',
    stageAr: 'ما قبل المرحلة',
    stageOrder: 0,
    language: 'ar',
    tags: ['acknowledgment', 'first-contact', 'automated'],
    contentEn: '',
    contentAr: `مرحباً {{customer_name}}! 👋

شكراً لاهتمامك بسيارات {{brand_name}} من المجدوعي للسيارات.

تم استلام طلبك وسيتواصل معك أحد مستشارينا خلال دقائق.

للاستفسارات العاجلة: {{branch_phone}}

المجدوعي للسيارات 🚗`,
    variables: ['customer_name', 'brand_name', 'branch_phone']
  },
  {
    id: 'wa-lead-ack-en',
    title: 'Lead Acknowledgment (English)',
    titleAr: 'تأكيد استلام الطلب (إنجليزي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Pre-Stage',
    stageAr: 'ما قبل المرحلة',
    stageOrder: 0,
    language: 'en',
    tags: ['acknowledgment', 'first-contact', 'automated'],
    contentEn: `Hello {{customer_name}}! 👋

Thank you for your interest in {{brand_name}} vehicles from Almajdouie Motors.

Your inquiry has been received and one of our consultants will contact you within minutes.

For urgent inquiries: {{branch_phone}}

Almajdouie Motors 🚗`,
    variables: ['customer_name', 'brand_name', 'branch_phone']
  },
  {
    id: 'wa-first-contact-ar',
    title: 'First Contact Follow-up (Arabic)',
    titleAr: 'متابعة أول تواصل (عربي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 1: New / Unworked',
    stageAr: 'المرحلة 1: جديد / غير معالج',
    stageOrder: 1,
    language: 'ar',
    tags: ['follow-up', 'stage-1', 'outbound'],
    contentEn: '',
    contentAr: `السلام عليكم {{customer_name}} 👋

حاولت التواصل معك بخصوص استفسارك عن سيارات {{brand_name}}.

هل يناسبك وقت محدد للتحدث؟ يسعدني مساعدتك في:
✅ معرفة الأسعار والعروض
✅ حجز موعد تجربة قيادة
✅ الإجابة على استفساراتك

{{agent_name}}
مستشار المبيعات - المجدوعي للسيارات
📞 {{agent_phone}}`,
    variables: ['customer_name', 'brand_name', 'agent_name', 'agent_phone']
  },
  {
    id: 'wa-first-contact-en',
    title: 'First Contact Follow-up (English)',
    titleAr: 'متابعة أول تواصل (إنجليزي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 1: New / Unworked',
    stageAr: 'المرحلة 1: جديد / غير معالج',
    stageOrder: 1,
    language: 'en',
    tags: ['follow-up', 'stage-1', 'outbound'],
    contentEn: `Hello {{customer_name}} 👋

I tried reaching you regarding your inquiry about {{brand_name}} vehicles.

Is there a convenient time to speak? I'd be happy to help you with:
✅ Pricing and current offers
✅ Booking a test drive
✅ Answering any questions

{{agent_name}}
Sales Consultant - Almajdouie Motors
📞 {{agent_phone}}`,
    variables: ['customer_name', 'brand_name', 'agent_name', 'agent_phone']
  },
  {
    id: 'wa-appt-confirm-ar',
    title: 'Appointment Confirmation (Arabic)',
    titleAr: 'تأكيد الموعد (عربي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 4: Qualified',
    stageAr: 'المرحلة 4: مؤهل',
    stageOrder: 4,
    language: 'ar',
    tags: ['appointment', 'confirmation', 'stage-4'],
    contentEn: '',
    contentAr: `مرحباً {{customer_name}}! ✅

تم تأكيد موعدك في المجدوعي للسيارات:

📅 التاريخ: {{appointment_date}}
🕐 الوقت: {{appointment_time}}
📍 الفرع: {{branch_name}}
👤 مستشارك: {{advisor_name}}
📞 للتواصل المباشر: {{advisor_phone}}

📍 الموقع: {{branch_map_link}}

يرجى إحضار:
• رخصة القيادة
• الهوية الوطنية

نتطلع لرؤيتك! 🚗
المجدوعي للسيارات`,
    variables: ['customer_name', 'appointment_date', 'appointment_time', 'branch_name', 'advisor_name', 'advisor_phone', 'branch_map_link']
  },
  {
    id: 'wa-appt-confirm-en',
    title: 'Appointment Confirmation (English)',
    titleAr: 'تأكيد الموعد (إنجليزي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 4: Qualified',
    stageAr: 'المرحلة 4: مؤهل',
    stageOrder: 4,
    language: 'en',
    tags: ['appointment', 'confirmation', 'stage-4'],
    contentEn: `Hello {{customer_name}}! ✅

Your appointment at Almajdouie Motors is confirmed:

📅 Date: {{appointment_date}}
🕐 Time: {{appointment_time}}
📍 Branch: {{branch_name}}
👤 Your Consultant: {{advisor_name}}
📞 Direct Contact: {{advisor_phone}}

📍 Location: {{branch_map_link}}

Please bring:
• Driving License
• National ID

We look forward to seeing you! 🚗
Almajdouie Motors`,
    variables: ['customer_name', 'appointment_date', 'appointment_time', 'branch_name', 'advisor_name', 'advisor_phone', 'branch_map_link']
  },
  {
    id: 'wa-appt-reminder-24h',
    title: 'Appointment Reminder – 24h (Both)',
    titleAr: 'تذكير الموعد - 24 ساعة (ثنائي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 5: Appointment Scheduled',
    stageAr: 'المرحلة 5: الموعد مجدول',
    stageOrder: 5,
    language: 'both',
    tags: ['reminder', 'stage-5', 'automated'],
    contentEn: `Hello {{customer_name}}! ⏰

Friendly reminder – your appointment at Almajdouie Motors is tomorrow!

📅 {{appointment_date}} at {{appointment_time}}
📍 {{branch_name}} – {{branch_address}}
👤 {{advisor_name}} will be your consultant

Need to reschedule? Reply to this message or call {{advisor_phone}}

See you tomorrow! 🚗`,
    contentAr: `مرحباً {{customer_name}}! ⏰

تذكير ودي - موعدك في المجدوعي للسيارات غداً!

📅 {{appointment_date}} الساعة {{appointment_time}}
📍 {{branch_name}} - {{branch_address}}
👤 {{advisor_name}} سيكون مستشارك

هل تريد إعادة الجدولة؟ رد على هذه الرسالة أو اتصل بـ {{advisor_phone}}

نراك غداً! 🚗`,
    variables: ['customer_name', 'appointment_date', 'appointment_time', 'branch_name', 'branch_address', 'advisor_name', 'advisor_phone']
  },
  {
    id: 'wa-quote-follow-en',
    title: 'Quote Follow-up (English)',
    titleAr: 'متابعة عرض الأسعار (إنجليزي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 7: Quote Sent',
    stageAr: 'المرحلة 7: تم إرسال عرض الأسعار',
    stageOrder: 7,
    language: 'en',
    tags: ['quote', 'follow-up', 'stage-7'],
    contentEn: `Hello {{customer_name}} 👋

I hope you had a chance to review the quote for {{vehicle_model}}.

Do you have any questions about:
💰 Pricing or payment options
🔄 Trade-in valuation
📋 Finance terms

I'm here to help make this the right decision for you.

{{advisor_name}}
📞 {{advisor_phone}}`,
    variables: ['customer_name', 'vehicle_model', 'advisor_name', 'advisor_phone']
  },
  {
    id: 'wa-delivery-confirm-ar',
    title: 'Delivery Confirmation (Arabic)',
    titleAr: 'تأكيد التسليم (عربي)',
    category: 'WhatsApp',
    categoryAr: 'واتساب',
    type: 'whatsapp',
    stage: 'Stage 10: Delivery & Post-Sale',
    stageAr: 'المرحلة 10: التسليم وما بعد البيع',
    stageOrder: 10,
    language: 'ar',
    tags: ['delivery', 'stage-10', 'celebration'],
    contentEn: '',
    contentAr: `مبروك {{customer_name}}! 🎉🚗

يسعدنا تأكيد استلامك لـ {{vehicle_model}} الجديدة.

🔑 رقم اللوحة: {{plate_number}}
📋 رقم العقد: {{contract_number}}
🛡️ انتهاء الضمان: {{warranty_expiry}}

للمساعدة أو الاستفسار:
📞 {{service_phone}}
📧 {{service_email}}

أول صيانة مجانية بعد {{first_service_km}} كم أو {{first_service_months}} أشهر.

نتمنى لك قيادة آمنة وممتعة! 🌟
فريق المجدوعي للسيارات`,
    variables: ['customer_name', 'vehicle_model', 'plate_number', 'contract_number', 'warranty_expiry', 'service_phone', 'service_email', 'first_service_km', 'first_service_months']
  },

  // ── SMS Templates ──
  {
    id: 'sms-missed-call-ar',
    title: 'Missed Call Follow-up SMS (Arabic)',
    titleAr: 'رسالة SMS متابعة مكالمة فائتة (عربي)',
    category: 'SMS',
    categoryAr: 'رسالة قصيرة',
    type: 'sms',
    stage: 'Stage 1: New / Unworked',
    stageAr: 'المرحلة 1: جديد / غير معالج',
    stageOrder: 1,
    language: 'ar',
    tags: ['sms', 'missed-call', 'stage-1'],
    contentEn: '',
    contentAr: 'المجدوعي للسيارات: حاولنا الاتصال بك بخصوص {{brand_name}}. للتواصل: {{agent_phone}} أو رد على هذه الرسالة.',
    variables: ['brand_name', 'agent_phone']
  },
  {
    id: 'sms-appt-confirm-ar',
    title: 'Appointment Confirmation SMS (Arabic)',
    titleAr: 'رسالة تأكيد الموعد (عربي)',
    category: 'SMS',
    categoryAr: 'رسالة قصيرة',
    type: 'sms',
    stage: 'Stage 4: Qualified',
    stageAr: 'المرحلة 4: مؤهل',
    stageOrder: 4,
    language: 'ar',
    tags: ['sms', 'appointment', 'stage-4'],
    contentEn: '',
    contentAr: 'المجدوعي: موعدك {{appointment_date}} الساعة {{appointment_time}} في فرع {{branch_name}}. مستشارك {{advisor_name}}. للتواصل: {{advisor_phone}}',
    variables: ['appointment_date', 'appointment_time', 'branch_name', 'advisor_name', 'advisor_phone']
  },

  // ── Checklists ──
  {
    id: 'chk-showroom-prep',
    title: 'Showroom Visit Preparation Checklist',
    titleAr: 'قائمة تحضير زيارة المعرض',
    category: 'Checklist',
    categoryAr: 'قائمة مراجعة',
    type: 'checklist',
    stage: 'Stage 5: Appointment Scheduled',
    stageAr: 'المرحلة 5: الموعد مجدول',
    stageOrder: 5,
    language: 'both',
    tags: ['showroom', 'preparation', 'advisor'],
    contentEn: `SHOWROOM VISIT PREPARATION CHECKLIST
Sales Advisor – Complete Before Customer Arrival

PRE-VISIT (Day before)
☐ Review lead profile and vehicle interest in CRM
☐ Confirm appointment with customer (24h reminder sent)
☐ Check vehicle availability and pre-position preferred model
☐ Print/prepare product brochures for interested model
☐ Review customer's trade-in vehicle if applicable

MORNING OF VISIT
☐ Send 3-hour reminder WhatsApp
☐ Confirm test drive vehicle is clean and fueled
☐ Brief Captain on customer profile and vehicle preference
☐ Prepare quote template with customer details
☐ Ensure finance pre-check is ready if needed

UPON ARRIVAL
☐ Greet customer by name at entrance
☐ Offer welcome refreshments
☐ Begin needs analysis conversation (SPIN framework)
☐ Record all qualification fields in CRM`,
    variables: [],
    notes: 'Complete all pre-visit items before 8:30 AM on day of appointment',
    notesAr: 'أكمل جميع عناصر ما قبل الزيارة قبل الساعة 8:30 صباحاً يوم الموعد'
  },
  {
    id: 'chk-delivery',
    title: 'Vehicle Delivery Checklist',
    titleAr: 'قائمة تسليم المركبة',
    category: 'Checklist',
    categoryAr: 'قائمة مراجعة',
    type: 'checklist',
    stage: 'Stage 10: Delivery & Post-Sale',
    stageAr: 'المرحلة 10: التسليم وما بعد البيع',
    stageOrder: 10,
    language: 'both',
    tags: ['delivery', 'pdi', 'coordinator'],
    contentEn: `VEHICLE DELIVERY CHECKLIST
Delivery Coordinator – Complete for Every Delivery

PDI (Pre-Delivery Inspection)
☐ Exterior: no scratches, dents, or paint issues
☐ Interior: seats, dashboard, all controls functional
☐ All fluids at correct levels
☐ Tyre pressure set to manufacturer spec
☐ All accessories fitted and functional

DOCUMENTATION
☐ Sales contract signed by customer
☐ Vehicle registration completed
☐ Insurance certificate provided
☐ Warranty booklet handed over
☐ Service book stamped with delivery date

DELIVERY CEREMONY
☐ Vehicle clean and presented in showroom/delivery bay
☐ Feature walkthrough completed
☐ First service reminder explained
☐ Emergency contact numbers provided
☐ Customer satisfaction photo/video (with consent)

POST-DELIVERY
☐ Day 2 follow-up call scheduled in CRM
☐ Survey link sent to customer
☐ CRM stage updated to Stage 10 / Delivered`,
    variables: [],
    notes: 'PDI must be completed by qualified technician at least 24h before delivery',
    notesAr: 'يجب إجراء فحص PDI بواسطة فني مؤهل قبل 24 ساعة من التسليم على الأقل'
  },
  {
    id: 'chk-qualification',
    title: 'Lead Qualification Checklist',
    titleAr: 'قائمة تأهيل العميل المحتمل',
    category: 'Checklist',
    categoryAr: 'قائمة مراجعة',
    type: 'checklist',
    stage: 'Stage 3: Contacted',
    stageAr: 'المرحلة 3: تم التواصل',
    stageOrder: 3,
    language: 'both',
    tags: ['qualification', 'stage-3', 'bdc'],
    contentEn: `LEAD QUALIFICATION CHECKLIST (STAGE 3)
BDC Agent – Complete During Qualification Call

MUST CAPTURE (Required Fields)
☐ Vehicle of interest confirmed (brand + model)
☐ Purchase timeline established (immediate / 1-3 months / 3-6 months / 6+)
☐ Budget range indicated (approximate)
☐ Finance required? (Yes / No / Undecided)
☐ Trade-in vehicle? (Yes / No)
☐ Preferred branch/location
☐ Decision-maker confirmed (self / spouse / family)

SCORING CRITERIA
☐ Hot (Score 80-100): Timeline < 1 month + Budget clear + Finance ready → Priority appointment
☐ Warm (Score 60-79): Timeline 1-3 months → Standard appointment booking
☐ Cold (Score < 60): Timeline 3+ months → Nurture campaign enrollment

NEXT ACTION
☐ If Hot/Warm: Offer 3 appointment slots
☐ If Cold: Add to nurture sequence + set 30-day follow-up
☐ Update all qualification fields in CRM before saving`,
    variables: []
  },

  // ── Call Scripts ──
  {
    id: 'script-opening-en',
    title: 'BDC Opening Call Script (English)',
    titleAr: 'نص افتتاح مكالمة BDC (إنجليزي)',
    category: 'Call Script',
    categoryAr: 'نص مكالمة',
    type: 'script',
    stage: 'Stage 1: New / Unworked',
    stageAr: 'المرحلة 1: جديد / غير معالج',
    stageOrder: 1,
    language: 'en',
    tags: ['opening', 'bdc', 'script'],
    contentEn: `BDC OPENING CALL SCRIPT – FIRST CONTACT

OPENING (5 seconds)
"Hello, may I speak with [Customer Name]?"
[Confirmed] → "Hello [Name], this is [Your Name] from Almajdouie Motors. How are you today?"

PURPOSE (10 seconds)
"I'm calling regarding your inquiry about [Brand/Model] vehicles. You [submitted a form / messaged us on WhatsApp] — is this a good time to speak for a few minutes?"

IF YES → Continue to qualification
IF NO → "No problem! When would be a better time to call back? I can reach you [morning/afternoon/evening]."
[Get callback time] → "Perfect, I'll call you [date/time]. Have a great day!"

QUALIFICATION (if they have time)
1. "What specifically caught your attention about [vehicle]?"
2. "Are you looking to purchase soon, or still in the research phase?"
3. "Would you be interested in a test drive at our nearest branch?"

CLOSING
"I'll book a slot for you at [Branch] on [Date] at [Time] – does that work?"
[Confirmed] → "Perfect! I'll send you a confirmation on WhatsApp right now."`,
    variables: ['customer_name', 'agent_name', 'brand_name', 'vehicle_model', 'branch_name']
  },
  {
    id: 'script-objection-price-en',
    title: 'Price Objection Handling Script',
    titleAr: 'نص التعامل مع اعتراض السعر',
    category: 'Call Script',
    categoryAr: 'نص مكالمة',
    type: 'script',
    stage: 'Stage 7: Quote Sent',
    stageAr: 'المرحلة 7: تم إرسال عرض الأسعار',
    stageOrder: 7,
    language: 'en',
    tags: ['objection', 'price', 'negotiation'],
    contentEn: `PRICE OBJECTION HANDLING – APPROVED SCRIPT

OBJECTION: "The price is too high" / "I found it cheaper elsewhere"

STEP 1 – ACKNOWLEDGE (Don't argue)
"I completely understand. Price is definitely an important factor in this decision."

STEP 2 – CLARIFY THE COMPARISON
"Can I ask – are you comparing the same model and trim level, or a different variant?"
[If different] → Explain the difference in features/value

STEP 3 – VALUE REFRAME
"Let me highlight what's included in our price:
• [Warranty period] full warranty
• [Service package] free service
• [Finance rate] competitive finance rate
• [Trade-in offer] your trade-in at market value"

STEP 4 – EXPLORE FLEXIBILITY
"Is it the monthly payment or the total price that's the main concern?"
[Monthly] → "Let me check if we can extend the terms to bring it down"
[Total] → "I can speak to my manager about what we can do – what would make this work for you?"

STEP 5 – ESCALATION (if needed)
"Let me get my Sales Manager involved to see what we can offer you."
→ Escalate to Sales Manager with deal summary`,
    variables: [],
    notes: 'Do not offer discount before manager approval. Discount authority: Advisor up to 2%, Manager up to 5%'
  },

  // ── Reports/Forms ──
  {
    id: 'form-daily-activity',
    title: 'Daily Activity Report Template',
    titleAr: 'قالب تقرير النشاط اليومي',
    category: 'Report',
    categoryAr: 'تقرير',
    type: 'report',
    stage: 'All Stages',
    stageAr: 'جميع المراحل',
    stageOrder: 99,
    language: 'both',
    tags: ['report', 'daily', 'kpi'],
    contentEn: `DAILY ACTIVITY REPORT – BDC AGENT
Date: ___________  Agent: ___________  Branch: ___________

CONTACT ACTIVITY
New leads received:       ___
Calls made:               ___
Calls connected:          ___
WhatsApp sent:            ___
WhatsApp responses:       ___

PIPELINE MOVEMENT
Leads moved to Stage 3 (Contacted):    ___
Leads moved to Stage 4 (Qualified):    ___
Appointments booked:                   ___
Leads marked Unreachable:              ___
Leads moved to Nurture:                ___

SLA COMPLIANCE
Leads responded within SLA:       ___  of  ___  (Target: 100%)
Cadence completion rate:           ___%                (Target: 100%)
SLA breaches today:                ___

ISSUES / ESCALATIONS
_______________________________________________
_______________________________________________

SUPERVISOR SIGN-OFF: _______________________`,
    variables: []
  },
  {
    id: 'form-escalation-log',
    title: 'Escalation Log Template',
    titleAr: 'قالب سجل التصعيد',
    category: 'Report',
    categoryAr: 'تقرير',
    type: 'form',
    stage: 'All Stages',
    stageAr: 'جميع المراحل',
    stageOrder: 99,
    language: 'both',
    tags: ['escalation', 'compliance', 'log'],
    contentEn: `ESCALATION LOG
Date/Time: ___________  Escalated By: ___________

LEAD DETAILS
Lead ID:        ___________
Customer Name:  ___________
Stage:          ___________
Assigned Agent: ___________

ESCALATION REASON
☐ SLA Breach – First Contact
☐ SLA Breach – Stage Progression
☐ Data Quality Issue
☐ Customer Complaint
☐ Duplicate Lead Conflict
☐ After-Hours Handling Issue
☐ Other: ___________

ESCALATION CODE (from Exception Registry)
EX Code: ___  Description: _______________

ESCALATED TO
☐ BDC Supervisor   ☐ Sales Manager   ☐ Branch Manager
☐ CRM Administrator   ☐ Quality Analyst   ☐ Operations Manager

ACTION TAKEN
_______________________________________________
_______________________________________________

RESOLUTION
☐ Resolved   ☐ Pending   ☐ Escalated Further
Resolution Notes: _______________________________________________

Manager Signature: _______________________  Date: ___________`,
    variables: []
  }
];

// ─── Tools Data ───────────────────────────────────────────────────────────────
const TOOL_CARDS: ToolCard[] = [
  {
    id: 'pipeline-viz',
    title: 'Pipeline Visualizer',
    titleAr: 'مرئي مسار المبيعات',
    description: 'Interactive 11-stage pipeline with SLA timers, field requirements, and role assignments for each stage.',
    descriptionAr: 'مسار تفاعلي من 11 مرحلة مع مؤقتات SLA ومتطلبات الحقول وتعيينات الأدوار لكل مرحلة.',
    category: 'Pipeline',
    categoryAr: 'المسار',
    icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
    link: '/pipeline',
    items: ['11 pipeline stages', 'SLA indicators', 'RACI matrix', 'Stage transitions'],
    itemsAr: ['11 مرحلة في المسار', 'مؤشرات SLA', 'مصفوفة RACI', 'انتقالات المراحل'],
    color: 'from-blue-50 to-blue-100 border-blue-200'
  },
  {
    id: 'sla-calculator',
    title: 'SLA Reference Guide',
    titleAr: 'دليل مرجع اتفاقيات مستوى الخدمة',
    description: 'Complete SLA targets for all 8 lead sources and 11 pipeline stages, with escalation thresholds.',
    descriptionAr: 'أهداف SLA الكاملة لجميع مصادر العملاء الـ 8 ومراحل المسار الـ 11، مع عتبات التصعيد.',
    category: 'Compliance',
    categoryAr: 'الامتثال',
    icon: <Clock className="w-6 h-6 text-green-600" />,
    link: '/sla-catalogue',
    items: ['8 channel SLAs', '11 stage SLAs', '6 escalation rules', 'Breach thresholds'],
    itemsAr: ['8 اتفاقيات قناة', '11 اتفاقية مرحلة', '6 قواعد تصعيد', 'عتبات الخرق'],
    color: 'from-green-50 to-green-100 border-green-200'
  },
  {
    id: 'crm-config',
    title: 'CRM Field Configuration Guide',
    titleAr: 'دليل تكوين حقول CRM',
    description: 'Stage-by-stage mandatory fields, validation rules, and automation triggers for CRM administrators.',
    descriptionAr: 'الحقول الإلزامية مرحلة بمرحلة وقواعد التحقق ومحفزات الأتمتة لمديري CRM.',
    category: 'System',
    categoryAr: 'النظام',
    icon: <Zap className="w-6 h-6 text-purple-600" />,
    link: '/crm',
    items: ['75 automation rules', '10 CRM stages', 'Field validation matrix', 'Integration map'],
    itemsAr: ['75 قاعدة أتمتة', '10 مراحل CRM', 'مصفوفة تحقق الحقول', 'خريطة التكامل'],
    color: 'from-purple-50 to-purple-100 border-purple-200'
  },
  {
    id: 'roles-raci',
    title: 'RACI Matrix & Org Chart',
    titleAr: 'مصفوفة RACI والهيكل التنظيمي',
    description: '10-role RACI matrix across all pipeline stages, coverage model, and governance links.',
    descriptionAr: 'مصفوفة RACI لـ 10 أدوار عبر جميع مراحل المسار، نموذج التغطية، وروابط الحوكمة.',
    category: 'Governance',
    categoryAr: 'الحوكمة',
    icon: <Users className="w-6 h-6 text-orange-600" />,
    link: '/roles',
    items: ['10 role profiles', 'RACI per stage', 'Coverage model', 'Escalation chain'],
    itemsAr: ['10 ملفات أدوار', 'RACI لكل مرحلة', 'نموذج التغطية', 'سلسلة التصعيد'],
    color: 'from-orange-50 to-orange-100 border-orange-200'
  },
  {
    id: 'policies-ref',
    title: 'Policies & Governance Reference',
    titleAr: 'مرجع السياسات والحوكمة',
    description: 'Data quality policies, escalation rules, exception codes, and brand-specific governance.',
    descriptionAr: 'سياسات جودة البيانات وقواعد التصعيد ورموز الاستثناءات والحوكمة الخاصة بكل علامة تجارية.',
    category: 'Governance',
    categoryAr: 'الحوكمة',
    icon: <Shield className="w-6 h-6 text-red-600" />,
    link: '/policies',
    items: ['6 escalation rules', '10 exception codes', 'Data quality policy', 'SLA compliance guide'],
    itemsAr: ['6 قواعد تصعيد', '10 رموز استثناء', 'سياسة جودة البيانات', 'دليل الامتثال SLA'],
    color: 'from-red-50 to-red-100 border-red-200'
  },
  {
    id: 'sops-ref',
    title: 'SOPs Reference Library',
    titleAr: 'مكتبة مرجع إجراءات التشغيل الموحدة',
    description: '16 standard operating procedures covering all pipeline stages and key exception scenarios.',
    descriptionAr: '16 إجراء تشغيل موحد يغطي جميع مراحل المسار وسيناريوهات الاستثناء الرئيسية.',
    category: 'Operations',
    categoryAr: 'العمليات',
    icon: <BookOpen className="w-6 h-6 text-teal-600" />,
    link: '/sops',
    items: ['11 stage SOPs', '5 scenario SOPs', 'Step-by-step flows', 'Role assignments'],
    itemsAr: ['11 إجراء مراحل', '5 إجراءات سيناريوهات', 'تدفقات خطوة بخطوة', 'تعيينات الأدوار'],
    color: 'from-teal-50 to-teal-100 border-teal-200'
  },
  {
    id: 'performance-dash',
    title: 'Performance Dashboard',
    titleAr: 'لوحة الأداء',
    description: 'Live KPI dashboards with scorecards, trend analysis, and target tracking for all roles.',
    descriptionAr: 'لوحات KPI المباشرة مع بطاقات الأداء وتحليل الاتجاهات وتتبع الأهداف لجميع الأدوار.',
    category: 'Analytics',
    categoryAr: 'التحليلات',
    icon: <BarChart2 className="w-6 h-6 text-indigo-600" />,
    link: '/performance',
    items: ['KPI overview', 'Role scorecards', 'Trend charts', 'Target vs actual'],
    itemsAr: ['نظرة عامة KPI', 'بطاقات أداء الأدوار', 'مخططات الاتجاهات', 'الهدف مقابل الفعلي'],
    color: 'from-indigo-50 to-indigo-100 border-indigo-200'
  },
  {
    id: 'implementation-tracker',
    title: 'Implementation Tracker',
    titleAr: 'متتبع التنفيذ',
    description: '6-phase implementation guide with progress tracker and phase completion dashboard.',
    descriptionAr: 'دليل تنفيذ من 6 مراحل مع متتبع التقدم ولوحة اكتمال المراحل.',
    category: 'Operations',
    categoryAr: 'العمليات',
    icon: <Package className="w-6 h-6 text-pink-600" />,
    link: '/implementation',
    items: ['6-phase roadmap', 'Progress tracker', 'Phase deliverables', 'Go-live checklist'],
    itemsAr: ['خارطة طريق 6 مراحل', 'متتبع التقدم', 'مخرجات المراحل', 'قائمة الإطلاق'],
    color: 'from-pink-50 to-pink-100 border-pink-200'
  }
];

// ─── Helper functions ─────────────────────────────────────────────────────────
const typeColors: Record<string, string> = {
  whatsapp: 'bg-green-100 text-green-800',
  sms: 'bg-blue-100 text-blue-800',
  email: 'bg-indigo-100 text-indigo-800',
  script: 'bg-orange-100 text-orange-800',
  checklist: 'bg-purple-100 text-purple-800',
  form: 'bg-teal-100 text-teal-800',
  report: 'bg-yellow-100 text-yellow-800'
};

const typeIcons: Record<string, React.ReactNode> = {
  whatsapp: <MessageSquare className="w-4 h-4" />,
  sms: <Phone className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  script: <FileText className="w-4 h-4" />,
  checklist: <ClipboardList className="w-4 h-4" />,
  form: <FileText className="w-4 h-4" />,
  report: <BarChart2 className="w-4 h-4" />
};

const stageColors: Record<number, string> = {
  0: 'bg-gray-100 text-gray-700',
  1: 'bg-amber-100 text-amber-800',
  2: 'bg-red-100 text-red-800',
  3: 'bg-orange-100 text-orange-800',
  4: 'bg-blue-100 text-blue-800',
  5: 'bg-indigo-100 text-indigo-800',
  6: 'bg-violet-100 text-violet-800',
  7: 'bg-purple-100 text-purple-800',
  8: 'bg-pink-100 text-pink-800',
  9: 'bg-rose-100 text-rose-800',
  10: 'bg-green-100 text-green-800',
  99: 'bg-slate-100 text-slate-700'
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ToolsTemplates = () => {
  const [activeTab, setActiveTab] = useState<'tools' | 'templates' | 'scripts' | 'checklists'>('tools');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType] = useState<string>('all');
  const [selectedStage, setSelectedStage] = useState<string>('all');
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'ar'>('en');

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };


  const filteredTemplates = TEMPLATES.filter(t => {
    const matchSearch = !searchQuery ||
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.titleAr.includes(searchQuery) ||
      t.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchType = selectedType === 'all' || t.category.toLowerCase() === selectedType.toLowerCase();
    const matchStage = selectedStage === 'all' || t.stageOrder.toString() === selectedStage;
    return matchSearch && matchType && matchStage;
  });

  const messagingTemplates = filteredTemplates.filter(t => ['whatsapp', 'sms', 'email'].includes(t.type));
  const scriptTemplates = filteredTemplates.filter(t => t.type === 'script');
  const checklistTemplates = filteredTemplates.filter(t => ['checklist', 'form', 'report'].includes(t.type));

  return (
    <div className={`min-h-screen bg-gray-50 ${lang === 'ar' ? 'rtl font-arabic' : 'ltr'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 via-teal-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2 text-teal-200 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {lang === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <button
              onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm transition-colors"
            >
              <Globe className="w-4 h-4" />
              {lang === 'en' ? 'العربية' : 'English'}
            </button>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Layers className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                {lang === 'ar' ? 'الأدوات والقوالب' : 'Tools & Templates'}
              </h1>
              <p className="text-teal-200 mt-1">
                {lang === 'ar'
                  ? 'مكتبة القوالب · قوائم المراجعة · نصوص المكالمات · تقارير الأداء'
                  : 'Template library · Checklists · Call scripts · Performance reports'}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: lang === 'ar' ? 'أداة تشغيلية' : 'Operational Tools', value: TOOL_CARDS.length, icon: <Package className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'قالب واتساب/SMS' : 'Message Templates', value: TEMPLATES.filter(t => ['whatsapp', 'sms'].includes(t.type)).length, icon: <MessageSquare className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'نص مكالمة' : 'Call Scripts', value: TEMPLATES.filter(t => t.type === 'script').length, icon: <Phone className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'قائمة/نموذج' : 'Checklists & Forms', value: TEMPLATES.filter(t => ['checklist', 'form', 'report'].includes(t.type)).length, icon: <ClipboardList className="w-5 h-5" /> }
            ].map((stat, i) => (
              <div key={i} className="bg-white/15 backdrop-blur rounded-xl p-4 flex items-center gap-3">
                <div className="text-teal-200">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-teal-200 text-xs">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'tools', label: lang === 'ar' ? 'الأدوات' : 'Tools & Modules', icon: <Package className="w-4 h-4" /> },
              { id: 'templates', label: lang === 'ar' ? 'قوالب الرسائل' : 'Message Templates', icon: <MessageSquare className="w-4 h-4" /> },
              { id: 'scripts', label: lang === 'ar' ? 'نصوص المكالمات' : 'Call Scripts', icon: <Phone className="w-4 h-4" /> },
              { id: 'checklists', label: lang === 'ar' ? 'القوائم والنماذج' : 'Checklists & Forms', icon: <ClipboardList className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search bar (for template tabs) */}
        {activeTab !== 'tools' && (
          <div className="mb-6 flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={lang === 'ar' ? 'ابحث في القوالب...' : 'Search templates...'}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedStage}
                onChange={e => setSelectedStage(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">{lang === 'ar' ? 'جميع المراحل' : 'All Stages'}</option>
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n.toString()}>
                    {n === 0 ? 'Pre-Stage' : `Stage ${n}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── TOOLS TAB ── */}
        {activeTab === 'tools' && (
          <div className="space-y-8">
            {/* Quick Access Note */}
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-teal-900">
                  {lang === 'ar' ? 'جميع الأدوات متوفرة مباشرة' : 'All tools available live'}
                </div>
                <div className="text-sm text-teal-700">
                  {lang === 'ar'
                    ? 'هذه الأدوات مرتبطة مباشرة بالسجلات وتعكس بيانات حقيقية من lead-pipeline.md وملفات السجل الأخرى.'
                    : 'These tools are directly wired to the registries and reflect live data from lead-pipeline.md and other registry files.'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {TOOL_CARDS.map(tool => (
                <div key={tool.id} className={`bg-gradient-to-br ${tool.color} border rounded-xl p-5 hover:shadow-md transition-all`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
                      {tool.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {lang === 'ar' ? tool.titleAr : tool.title}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {lang === 'ar' ? tool.categoryAr : tool.category}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    {lang === 'ar' ? tool.descriptionAr : tool.description}
                  </p>

                  <div className="space-y-1 mb-4">
                    {(lang === 'ar' ? tool.itemsAr : tool.items).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                        <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>

                  {tool.link && (
                    <Link
                      to={tool.link}
                      className="flex items-center justify-center gap-2 w-full bg-white border border-gray-200 rounded-lg py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {lang === 'ar' ? 'فتح الأداة' : 'Open Tool'}
                      <FileText className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Variable Legend */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {lang === 'ar' ? 'دليل متغيرات القالب' : 'Template Variable Guide'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {[
                  { var: '{{customer_name}}', desc: lang === 'ar' ? 'الاسم الكامل للعميل' : 'Customer full name' },
                  { var: '{{brand_name}}', desc: lang === 'ar' ? 'العلامة التجارية للمركبة' : 'Vehicle brand (Changan/Hyundai/Stellantis)' },
                  { var: '{{agent_name}}', desc: lang === 'ar' ? 'اسم وكيل BDC' : 'BDC agent name' },
                  { var: '{{advisor_name}}', desc: lang === 'ar' ? 'اسم مستشار المبيعات' : 'Sales advisor name' },
                  { var: '{{branch_name}}', desc: lang === 'ar' ? 'اسم الفرع' : 'Branch name' },
                  { var: '{{appointment_date}}', desc: lang === 'ar' ? 'تاريخ الموعد' : 'Appointment date' },
                  { var: '{{appointment_time}}', desc: lang === 'ar' ? 'وقت الموعد' : 'Appointment time' },
                  { var: '{{vehicle_model}}', desc: lang === 'ar' ? 'طراز المركبة المحددة' : 'Specific vehicle model' },
                  { var: '{{agent_phone}}', desc: lang === 'ar' ? 'رقم هاتف الوكيل' : 'Agent phone number' }
                ].map((v, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <code className="text-xs bg-gray-100 text-teal-700 px-1.5 py-0.5 rounded font-mono">{v.var}</code>
                    <span className="text-gray-600 text-xs">{v.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── TEMPLATES / SCRIPTS / CHECKLISTS TABS ── */}
        {(activeTab === 'templates' || activeTab === 'scripts' || activeTab === 'checklists') && (
          <div className="space-y-4">
            {(() => {
              const displayTemplates = activeTab === 'templates' ? messagingTemplates
                : activeTab === 'scripts' ? scriptTemplates
                : checklistTemplates;

              if (displayTemplates.length === 0) {
                return (
                  <div className="text-center py-12 text-gray-500">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p>{lang === 'ar' ? 'لا توجد نتائج مطابقة' : 'No matching templates found'}</p>
                  </div>
                );
              }

              return displayTemplates.map(template => {
                const isExpanded = expandedTemplate === template.id;
                const displayContent = lang === 'ar' && template.contentAr ? template.contentAr
                  : template.contentEn || template.contentAr || '';

                return (
                  <div key={template.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
                    {/* Card Header */}
                    <div
                      className="p-5 cursor-pointer"
                      onClick={() => setExpandedTemplate(isExpanded ? null : template.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${typeColors[template.type]}`}>
                            {typeIcons[template.type]}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">
                              {lang === 'ar' ? template.titleAr : template.title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[template.type]}`}>
                                {lang === 'ar' ? template.categoryAr : template.category}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${stageColors[template.stageOrder] || 'bg-gray-100 text-gray-600'}`}>
                                {lang === 'ar' ? template.stageAr : template.stage}
                              </span>
                              <span className="text-xs text-gray-400">
                                {template.language === 'both' ? '🌐 EN + AR' : template.language === 'ar' ? '🇸🇦 AR' : '🇬🇧 EN'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!isExpanded && displayContent && (
                            <button
                              onClick={e => { e.stopPropagation(); copyToClipboard(displayContent, template.id); }}
                              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                                copiedId === template.id
                                  ? 'bg-green-50 border-green-300 text-green-700'
                                  : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'
                              }`}
                            >
                              {copiedId === template.id ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copiedId === template.id ? (lang === 'ar' ? 'تم النسخ' : 'Copied!') : (lang === 'ar' ? 'نسخ' : 'Copy')}
                            </button>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1 mt-2 ml-11">
                        {template.tags.map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t border-gray-100">
                        {/* Variables Banner */}
                        {template.variables.length > 0 && (
                          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
                            <div className="flex items-center gap-2 text-xs text-amber-800">
                              <Tag className="w-3 h-3" />
                              <span className="font-medium">{lang === 'ar' ? 'المتغيرات المطلوبة:' : 'Required variables:'}</span>
                              <div className="flex flex-wrap gap-1">
                                {template.variables.map(v => (
                                  <code key={v} className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-mono">
                                    {'{{'}{v}{'}}'}
                                  </code>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Template Content */}
                        <div className="p-5">
                          {/* Language tabs if both */}
                          {template.language === 'both' && (
                            <div className="mb-4 flex gap-2">
                              <span className="text-sm font-medium text-gray-700">
                                {lang === 'ar' ? 'يتوفر بكلتا اللغتين:' : 'Available in both languages:'}
                              </span>
                            </div>
                          )}

                          {/* EN Content */}
                          {template.contentEn && (
                            <div className="mb-4">
                              {template.language === 'both' && (
                                <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                  🇬🇧 English Version
                                </div>
                              )}
                              <div className="relative">
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                                  {template.contentEn}
                                </pre>
                                <button
                                  onClick={() => copyToClipboard(template.contentEn, template.id + '-en')}
                                  className={`absolute top-2 right-2 flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
                                    copiedId === template.id + '-en'
                                      ? 'bg-green-50 border-green-300 text-green-700'
                                      : 'bg-white border-gray-300 text-gray-600 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'
                                  }`}
                                >
                                  {copiedId === template.id + '-en' ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  {copiedId === template.id + '-en' ? 'Copied!' : 'Copy'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* AR Content */}
                          {template.contentAr && (
                            <div className="mb-4">
                              {template.language === 'both' && (
                                <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                                  🇸🇦 النسخة العربية
                                </div>
                              )}
                              <div className="relative">
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed text-right dir-rtl">
                                  {template.contentAr}
                                </pre>
                                <button
                                  onClick={() => copyToClipboard(template.contentAr!, template.id + '-ar')}
                                  className={`absolute top-2 left-2 flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
                                    copiedId === template.id + '-ar'
                                      ? 'bg-green-50 border-green-300 text-green-700'
                                      : 'bg-white border-gray-300 text-gray-600 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700'
                                  }`}
                                >
                                  {copiedId === template.id + '-ar' ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  {copiedId === template.id + '-ar' ? 'تم' : 'نسخ'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {template.notes && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                              <div>
                                <div className="text-xs font-medium text-blue-800">
                                  {lang === 'ar' ? 'ملاحظة' : 'Note'}
                                </div>
                                <div className="text-xs text-blue-700">
                                  {lang === 'ar' ? template.notesAr : template.notes}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Download Button */}
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => {
                                const blob = new Blob(
                                  [template.contentEn + (template.contentAr ? '\n\n---\n\n' + template.contentAr : '')],
                                  { type: 'text/plain' }
                                );
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${template.id}.txt`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                              className="flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 border border-teal-300 hover:border-teal-500 px-3 py-1.5 rounded-lg transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              {lang === 'ar' ? 'تنزيل كملف نصي' : 'Download as .txt'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolsTemplates;
